import { useState, useEffect, memo } from "react";
import { 
  Wallet, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, 
  BarChart3, Plus, Bot, Send, AlertTriangle, Target, RefreshCw,
  Settings, Trash2, Mic, MicOff, ChevronDown, ChevronUp, Users, Bell, Zap, Eye,
  Crown, ShieldCheck, Clock, Brain, Crosshair, Flame, Star,
  Shield, Layers, Sparkles, Search
} from "lucide-react";
import { formatNumber, formatPercent, getSignal, getTickerCurrency } from "../utils/trading";
import TopConvictionsCompact from "./TopConvictionsCompact";
import PullbackCompact from "./PullbackCompact";
import Top5History from "./Top5History";
import ConfluenceScreener from "./ConfluenceScreener";
import TopPicksByTabCompact from "./TopPicksByTabCompact";
import DailyBriefing from "./DailyBriefing";
import PortfolioChart from "./PortfolioChart";
import OpportunitiesPanel from "./OpportunitiesPanel";
import MorningPocketPlays from "./MorningPocketPlays";

// Auto-detect country flag from ticker suffix when pos.flag is missing
const getTickerFlag = (ticker, posFlag) => {
  if (posFlag && posFlag.length > 0 && posFlag !== "US" && posFlag.codePointAt(0) > 127) return posFlag; // already emoji
  const t = ticker || "";
  if (t.endsWith(".DE")) return "🇩🇪";
  if (t.endsWith(".PA")) return "🇫🇷";
  if (t.endsWith(".CO")) return "🇩🇰";
  if (t.endsWith(".L")) return "🇬🇧";
  if (t.endsWith(".AS")) return "🇳🇱";
  if (t.endsWith(".SW")) return "🇨🇭";
  if (t.endsWith(".BR")) return "🇧🇪";
  if (t.endsWith(".HE")) return "🇫🇮";
  if (t.endsWith(".VI")) return "🇦🇹";
  if (t.endsWith(".MC")) return "🇪🇸";
  if (t.endsWith(".MI")) return "🇮🇹";
  if (t.endsWith(".OL")) return "🇳🇴";
  if (t.endsWith(".ST")) return "🇸🇪";
  if (t.endsWith(".T")) return "🇯🇵";
  if (t.endsWith(".HK")) return "🇭🇰";
  if (t.endsWith(".KS")) return "🇰🇷";
  if (t.endsWith(".IS")) return "🇹🇷";
  return "🇺🇸";
};


const formatEUR = (num) => {
  if (num === null || num === undefined) return "—";
  return `${num.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}€`;
};

const getEntryCurrencySymbol = (pos) => {
  const ec = pos.entry_currency;
  if (ec === "EUR") return "€";
  if (ec === "GBP") return "£";
  if (ec === "USD") return "$";
  if (ec) return ec + " ";
  return getTickerCurrency(pos.ticker);
};

function Sparkline({ values, width = 80, height = 28 }) {
  if (!values || values.length < 2) return <span className="text-slate-500 text-xs">—</span>;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values.map((v, i) => `${(i / (values.length - 1)) * width},${height - ((v - min) / range) * height}`).join(" ");
  const isUp = values[values.length - 1] >= values[0];
  return (
    <svg width={width} height={height} className="inline-block">
      <polyline points={points} fill="none" stroke={isUp ? "#22c55e" : "#ef4444"} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>
    </svg>
  );
}

function calcPickBudget(portfolioValue, pick) {
  if (!portfolioValue || portfolioValue <= 0) return null;
  const entry = pick.entry_price || pick.price;
  const stop = pick.stop_loss;
  const target = pick.target;
  if (!entry || entry <= 0 || !stop || stop <= 0) return null;
  
  const price = pick.price || entry;
  const signal = pick.action || "WATCHLIST";
  let riskPct = signal === "STRONG BUY" ? 0.025 : signal === "BUY" ? 0.02 : 0.01;
  
  // Stock type adjustment
  let cap;
  if (price < 1) { cap = "PENNY"; riskPct *= 0.3; }
  else if (price < 5) { cap = "MICRO"; riskPct *= 0.5; }
  else if (price < 20) { cap = "SMALL"; riskPct *= 0.7; }
  else { cap = "STD"; }
  
  // R/R boost
  const rr = pick.risk_reward || 0;
  if (rr >= 3) riskPct *= 1.2;
  else if (rr < 1.5 && rr > 0) riskPct *= 0.6;
  
  // RSI adjustment
  if (pick.rsi > 70) riskPct *= 0.7;
  
  riskPct = Math.max(0.003, Math.min(riskPct, 0.035));
  const riskPerShare = Math.abs(entry - stop);
  if (riskPerShare <= 0) return null;
  let posValue = Math.floor((portfolioValue * riskPct) / riskPerShare) * entry;
  const maxMap = { PENNY: 0.02, MICRO: 0.04, SMALL: 0.06, STD: 0.10 };
  const maxPct = signal === "STRONG BUY" ? maxMap[cap] * 1.3 : maxMap[cap];
  if (posValue > portfolioValue * maxPct) posValue = Math.floor(portfolioValue * maxPct);
  if (posValue < 50) return null;
  const gain = target > entry ? ((target - entry) / entry * posValue) : 0;
  const loss = riskPerShare / entry * posValue;
  return { amount: Math.round(posValue / 100) * 100 || 100, pct: ((posValue / portfolioValue) * 100).toFixed(1), gain: Math.round(gain), loss: Math.round(loss) };
}

function MarketStatusBar({ lastRefreshIso }) {
  const [status, setStatus] = useState(null);
  const [tick, setTick] = useState(0);
  const API = process.env.REACT_APP_BACKEND_URL;
  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const r = await fetch(`${API}/api/market/hours-status`);
        const d = await r.json();
        if (!cancelled) setStatus(d);
      } catch {
        // ignore
      }
    };
    fetchStatus();
    const iv = setInterval(fetchStatus, 60000); // status every 60s
    const tickIv = setInterval(() => setTick(t => t + 1), 5000); // "last refresh" every 5s
    return () => { cancelled = true; clearInterval(iv); clearInterval(tickIv); };
  }, [API]);

  // Compute human-readable "last refresh" age
  let ageLabel = "—";
  if (lastRefreshIso) {
    try {
      const ageSec = Math.max(0, Math.floor((Date.now() - new Date(lastRefreshIso).getTime()) / 1000));
      if (ageSec < 60) ageLabel = `${ageSec}s`;
      else if (ageSec < 3600) ageLabel = `${Math.floor(ageSec / 60)}m`;
      else ageLabel = `${Math.floor(ageSec / 3600)}h`;
    } catch { /* noop */ }
  }
  // Referenced tick to force re-render every 5s
  const _ = tick;

  if (!status) return null;
  const us = status.us || {};
  const eu = status.eu || {};

  const pill = (label, open, sub) => (
    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium ${
      open ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
           : "bg-slate-700/40 text-slate-400 border border-slate-600/40"
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${open ? "bg-emerald-400 animate-pulse" : "bg-slate-500"}`} />
      <span className="uppercase tracking-wider">{label}</span>
      {sub && <span className="text-slate-500 normal-case">· {sub}</span>}
    </div>
  );

  const usStateLabel = us.regular_open ? "Ouvert" : us.premarket ? "Pre-mkt" : us.afterhours ? "After-hrs" : "Fermé";
  const usSub = us.is_open ? "" : `ouvre ${us.opens_at_paris} Paris`;
  const euSub = eu.is_open ? `ferme ${eu.closes_at_paris}` : `ouvre ${eu.opens_at_paris}`;

  return (
    <div className="flex items-center gap-2 flex-wrap mb-3 px-1" data-testid="market-status-bar">
      {pill(`🇺🇸 US: ${usStateLabel}`, us.is_open, usSub)}
      {pill(`🇪🇺 EU: ${eu.is_open ? "Ouvert" : "Fermé"}`, eu.is_open, euSub)}
      <div className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium bg-slate-800/60 text-slate-400 border border-slate-700/40">
        <RefreshCw className="w-3 h-3" />
        <span>Prix live · MAJ il y a <b className="text-slate-200">{ageLabel}</b></span>
      </div>
    </div>
  );
}


export default memo(function PortfolioTab({
  totalValue, pnl, pnlPercent, dailyPnl, dailyPnlPct,
  realizedPnl,
  quotesLoading, hasPrices, lastUpdate,
  toEUR,
  portfolio, ownerPositions, ltCount, stCount, owner,
  strategyFilter, setStrategyFilter,
  brokerFxOverrides = {}, liveEurRate, updateBrokerFxOverride,
  t212Syncing, syncTrading212, openAddModal,
  portfolioCmd, setPortfolioCmd, portfolioCmdLoading, portfolioCmdResult,
  isListening, listenTarget, toggleVoice,
  sortCol, sortDir, setSortCol, setSortDir,
  getPrice, dailyChanges, athData, supportResistance, sparklines,
  earningsData, postMarketData, valuationData, marketStatus, closePrices,
  convictionScores = {},
  analyzePosition, handleDeletePosition,
  setEditingPosition, setModalMode, setShowPositionModal,
  onPortfolioCommand,
  onExpertAnalyzePosition,
  onExpertAnalysis,
  onFindOpportunity,
  watchlistAlerts, watchlistAlertsSummary, watchlistAlertsLoading,
  onScanWatchlist, onAnalyzeTicker, onTabChange,
  topPicks, topPicksAI, topPicksAILoading, onAnalyzeTopPicksAI,
  swingPicks,
  marketTiming,
  sectorHeatmap,
  sectorHeatmapLoading,
  onRefreshSectorHeatmap,
  sectorDrilldown, sectorDrilldownLoading, selectedSector,
  onOpenSectorDrilldown, onCloseSectorDrilldown,
  algoTopRated,
  sellSignals = {},
  analystRatings = {},
  briefingCache,
  setBriefingCache,
  pullbackOpps,
  pullbackLoading,
  onAnalyzePullback,
}) {
  const rPnl = realizedPnl?.total_realized_pnl || 0;
  const totalPnl = pnl + rPnl;
  const totalPnlPct = (totalValue + Math.abs(rPnl)) > 0 ? (totalPnl / ((totalValue - pnl) + (realizedPnl?.total_cost_basis || 0))) * 100 : 0;
  const [sectorsOpen, setSectorsOpen] = useState(false);
  // 2026-06-17 — Broker quick-filter. Click a broker card to filter the table.
  const [brokerFilter, setBrokerFilter] = useState(null);

  // Map broker -> { icon, color, accentBg, accentText, accentBorder }
  const BROKER_STYLES = {
    "Millennium": { icon: Crown, ring: "from-amber-400 to-orange-500", bg: "bg-amber-500/15", text: "text-amber-300", border: "border-amber-500/50" },
    "Trading 212": { icon: TrendingUp, ring: "from-sky-400 to-blue-500", bg: "bg-sky-500/15", text: "text-sky-300", border: "border-sky-500/50" },
    "Soc. Générale": { icon: ShieldCheck, ring: "from-red-400 to-rose-500", bg: "bg-red-500/15", text: "text-red-300", border: "border-red-500/50" },
    "N26": { icon: Sparkles, ring: "from-teal-400 to-emerald-500", bg: "bg-teal-500/15", text: "text-teal-300", border: "border-teal-500/50" },
    "Trade Republic": { icon: Crosshair, ring: "from-violet-400 to-purple-500", bg: "bg-violet-500/15", text: "text-violet-300", border: "border-violet-500/50" },
    "DEGIRO": { icon: Layers, ring: "from-lime-400 to-green-500", bg: "bg-lime-500/15", text: "text-lime-300", border: "border-lime-500/50" },
    "Interactive Brokers": { icon: Brain, ring: "from-cyan-400 to-blue-500", bg: "bg-cyan-500/15", text: "text-cyan-300", border: "border-cyan-500/50" },
  };
  const DEFAULT_BROKER_STYLE = { icon: Wallet, ring: "from-slate-400 to-slate-500", bg: "bg-slate-500/15", text: "text-slate-300", border: "border-slate-500/50" };

  return (
    <div className="animate-fade-in">
      {/* MARKET STATUS BAR — always visible at top */}
      <MarketStatusBar
        lastRefreshIso={
          Array.isArray(portfolio) && portfolio.length > 0
            ? portfolio.reduce((latest, p) => {
                const t = p.price_updated_at || p.last_updated;
                if (!t) return latest;
                if (!latest || new Date(t) > new Date(latest)) return t;
                return latest;
              }, null)
            : null
        }
      />

      {/* MORNING POCKET PLAYS — collapsible to save vertical space */}
      <details className="mb-4 sm:mb-6" data-testid="morning-plays-collapse">
        <summary className="cursor-pointer list-none">
          <div className="glass-card rounded-xl px-4 py-3 border border-emerald-500/30 flex items-center justify-between hover:border-emerald-500/50 transition-colors">
            <div className="flex items-center gap-3">
              <div className="text-emerald-400 text-xl">🎯</div>
              <div>
                <div className="text-sm font-black text-white uppercase tracking-tight">Morning Pocket Plays</div>
                <div className="text-[10px] text-slate-400">3 setups pré-planifiés du jour — clique pour déplier</div>
              </div>
            </div>
            <div className="text-xs text-slate-400">▾</div>
          </div>
        </summary>
        <div className="mt-3">
          <MorningPocketPlays owner={owner} onAnalyze={onAnalyzeTicker} />
        </div>
      </details>

      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4 sm:mb-6">
        {[{ label: "Valeur Totale", value: formatEUR(totalValue), icon: Wallet, gradient: "gradient-green", needsQuotes: true, testid: "total-value-card" },
          { label: "P&L Total",
            value: `${totalPnl >= 0 ? "+" : ""}${formatEUR(totalPnl)}`,
            sub: `${totalPnlPct >= 0 ? "+" : ""}${totalPnlPct.toFixed(1)}%  •  Jour ${dailyPnl >= 0 ? "+" : ""}${formatEUR(dailyPnl)} (${dailyPnlPct >= 0 ? "+" : ""}${dailyPnlPct.toFixed(2)}%)`,
            icon: totalPnl >= 0 ? TrendingUp : TrendingDown,
            gradient: totalPnl >= 0 ? "gradient-green" : "gradient-red",
            testid: "total-pnl-card",
            needsQuotes: true },
          { label: "Positions", value: portfolio.length, sub: `${portfolio.filter(p => getPrice(p) > p.avg).length} profitable`, icon: BarChart3, gradient: "gradient-blue", testid: "positions-card" },
        ].map((card, i) => {
          const isCardLoading = card.needsQuotes && !hasPrices;
          return (
          <div key={i} data-testid={card.testid || `stat-card-${i}`} className={`${card.gradient} rounded-xl p-3 sm:p-4 relative overflow-hidden`}>
            <card.icon className="absolute top-2 right-2 sm:top-3 sm:right-3 w-5 h-5 sm:w-7 sm:h-7 opacity-20" />
            <div className="text-[9px] sm:text-[10px] text-slate-300 font-medium uppercase tracking-wider mb-1">{card.label}</div>
            {isCardLoading ? (
              <div className="flex items-center gap-2 mt-1">
                <RefreshCw className="w-4 h-4 text-slate-300 animate-spin" />
                <span className="text-xs text-slate-400">Chargement...</span>
              </div>
            ) : (
              <>
                <div className="text-base sm:text-xl font-bold font-mono text-white">{card.value}</div>
                {card.sub && <div className="text-[10px] sm:text-xs text-slate-300 mt-0.5">{card.sub}</div>}
              </>
            )}
          </div>
          );
        })}
      </div>

      {/* ===== DAILY AI BRIEFING (with market indicators) ===== */}
      <DailyBriefing owner={owner} marketTiming={marketTiming} onAnalyzeTicker={onAnalyzeTicker} onTabChange={onTabChange} briefingCache={briefingCache} setBriefingCache={setBriefingCache} />

      {/* ===== INTELLIGENCE PANELS — GRID 4 COLONNES (Pullback + Top 10 + Top par Onglet + Top 5) ===== */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-4 sm:mb-6">
        <PullbackCompact
          opportunities={pullbackOpps}
          onAnalyze={onAnalyzeTicker}
          loading={pullbackLoading}
        />
        <TopConvictionsCompact topRated={algoTopRated || []} onAnalyze={onAnalyzeTicker} title="Top 10 du Jour" />
        <TopPicksByTabCompact owner={owner} onTabChange={onTabChange} onAnalyze={onAnalyzeTicker} />
        <Top5History onAnalyze={onAnalyzeTicker} />
      </div>

      {/* ===== SECTOR HEAT MAP (collapsible) ===== */}
      {sectorHeatmap?.sectors?.length > 0 && (
        <div className="glass-card rounded-xl p-3 sm:p-4 mb-4 border border-slate-700/30" data-testid="sector-heatmap-panel">
          <div className="flex items-center justify-between cursor-pointer" onClick={() => setSectorsOpen(!sectorsOpen)} data-testid="toggle-sectors">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-violet-400" />
              <span className="text-xs sm:text-sm font-black text-white uppercase tracking-tight">Secteurs</span>
              {sectorHeatmap.hot_sectors > 0 && (
                <span className="px-1.5 py-0.5 rounded text-[8px] font-black bg-orange-500/20 text-orange-300 border border-orange-500/30 animate-pulse">
                  {sectorHeatmap.hot_sectors} HOT
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {sectorHeatmap.cache_age_seconds != null && (
                <span className="text-[9px] text-slate-500">
                  {sectorHeatmap.cache_age_seconds < 60 ? "< 1 min" :
                   sectorHeatmap.cache_age_seconds < 3600 ? `${Math.floor(sectorHeatmap.cache_age_seconds / 60)} min` :
                   `${Math.floor(sectorHeatmap.cache_age_seconds / 3600)}h${Math.floor((sectorHeatmap.cache_age_seconds % 3600) / 60)}m`}
                </span>
              )}
              {sectorHeatmap.refreshing && (
                <span className="text-[8px] text-cyan-400 animate-pulse">rafraîchissement...</span>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); onRefreshSectorHeatmap(); }}
                disabled={sectorHeatmapLoading}
                className="p-1 rounded-md bg-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-600/50 transition-all disabled:opacity-40"
                data-testid="refresh-sector-heatmap">
                <RefreshCw className={`w-3 h-3 ${sectorHeatmapLoading ? 'animate-spin' : ''}`} />
              </button>
              {sectorsOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </div>
          </div>
          {sectorsOpen && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-1.5 sm:gap-2 mt-3">
            {sectorHeatmap.sectors.slice(0, 24).map((sec, i) => {
              const heat = sec.heat_score;
              const bg = heat > 5 ? "bg-emerald-500/15 border-emerald-500/30" :
                         heat > 0 ? "bg-emerald-500/[0.07] border-emerald-500/20" :
                         heat > -3 ? "bg-slate-500/10 border-slate-600/30" :
                         "bg-red-500/10 border-red-500/20";
              const textColor = heat > 5 ? "text-emerald-400" : heat > 0 ? "text-emerald-400/70" : heat > -3 ? "text-slate-400" : "text-red-400";
              return (
                <div key={sec.etf}
                  className={`rounded-lg p-2 border ${bg} transition-all hover:brightness-125 relative cursor-pointer`}
                  onClick={() => onOpenSectorDrilldown(sec.etf, sec.sector)}
                  data-testid={`sector-${sec.etf}`}>
                  {sec.is_hot && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-orange-500 animate-pulse" />
                  )}
                  <p className="text-[9px] sm:text-[10px] font-bold text-white truncate">{sec.sector}</p>
                  <div className="flex items-baseline gap-1 mt-0.5">
                    <span className={`text-xs sm:text-sm font-black ${textColor}`}>
                      {sec.perf_3d >= 0 ? "+" : ""}{sec.perf_3d}%
                    </span>
                    <span className="text-[7px] text-slate-600">3j</span>
                  </div>
                  <div className="flex gap-1.5 mt-0.5">
                    <span className={`text-[7px] font-semibold ${sec.perf_1d >= 0 ? "text-emerald-500/60" : "text-red-500/60"}`}>
                      {sec.perf_1d >= 0 ? "+" : ""}{sec.perf_1d}% 1j
                    </span>
                    <span className={`text-[7px] font-semibold ${sec.perf_5d >= 0 ? "text-emerald-500/60" : "text-red-500/60"}`}>
                      {sec.perf_5d >= 0 ? "+" : ""}{sec.perf_5d}% 5j
                    </span>
                  </div>
                  {sec.my_stocks_count > 0 && (
                    <p className="text-[7px] text-slate-500 mt-1 truncate">
                      {sec.my_stocks.slice(0, 3).map(s => s.ticker).join(", ")}
                      {sec.my_stocks_count > 3 && ` +${sec.my_stocks_count - 3}`}
                    </p>
                  )}
                </div>
              );
            })}
            </div>
          )}
        </div>
      )}

      {/* ===== SECTOR DRILLDOWN PANEL ===== */}
      {selectedSector && (
        <div className="glass-card rounded-xl p-3 sm:p-5 mb-4 border border-violet-500/30 relative overflow-hidden" data-testid="sector-drilldown-panel">
          <div className="absolute inset-0 bg-gradient-to-br from-violet-500/[0.04] via-transparent to-purple-500/[0.03]" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-violet-400" />
                <h3 className="text-sm font-black text-white">{selectedSector.name}</h3>
                <span className="text-[10px] text-slate-500">({selectedSector.etf})</span>
              </div>
              <button onClick={onCloseSectorDrilldown}
                className="px-2 py-1 rounded-lg bg-slate-700/50 text-slate-400 text-[10px] hover:bg-slate-600/50 transition-all"
                data-testid="close-sector-drilldown">
                Fermer
              </button>
            </div>

            {sectorDrilldownLoading ? (
              <div className="text-center py-6">
                <RefreshCw className="w-5 h-5 text-violet-400 animate-spin mx-auto mb-2" />
                <p className="text-xs text-slate-400">Chargement des meilleures actions...</p>
              </div>
            ) : sectorDrilldown?.stocks?.length > 0 ? (
              <div>
                {/* Signal summary bar */}
                {(() => {
                  const counts = {};
                  sectorDrilldown.stocks.forEach(s => { counts[s.swing_signal] = (counts[s.swing_signal] || 0) + 1; });
                  const signalOrder = [
                    ["SURVENTE", "bg-red-500/20 text-red-300 border-red-500/30"],
                    ["REBOND", "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"],
                    ["MOMENTUM", "bg-cyan-500/20 text-cyan-300 border-cyan-500/30"],
                    ["TREND UP", "bg-blue-500/20 text-blue-300 border-blue-500/30"],
                    ["NEUTRE", "bg-slate-700/30 text-slate-400 border-slate-600/30"],
                    ["SURACHAT", "bg-orange-500/20 text-orange-300 border-orange-500/30"],
                  ];
                  return (
                    <div className="flex flex-wrap gap-1.5 mb-3 pb-2 border-b border-slate-700/20">
                      {signalOrder.filter(([sig]) => counts[sig]).map(([sig, cls]) => (
                        <span key={sig} className={`px-2 py-0.5 rounded-full text-[8px] font-bold border ${cls}`}>
                          {sig} ({counts[sig]})
                        </span>
                      ))}
                      <span className="text-[8px] text-slate-500 self-center ml-1">Tri: opportunites en premier</span>
                    </div>
                  );
                })()}

                {/* Column headers */}
                <div className="hidden sm:grid grid-cols-[1.2fr_0.5fr_0.5fr_0.5fr_0.5fr_0.5fr_0.5fr_0.5fr] gap-1.5 px-2 pb-2 mb-1.5 border-b border-slate-700/30 text-[8px] text-slate-500 font-semibold uppercase tracking-wider">
                  <span>Action</span>
                  <span className="text-right">Prix</span>
                  <span className="text-right">1 Jour</span>
                  <span className="text-right">3 Jours</span>
                  <span className="text-right">5 Jours</span>
                  <span className="text-right">1 Mois</span>
                  <span className="text-center">RSI</span>
                  <span className="text-center">Signal</span>
                </div>

                <div className="space-y-1">
                {sectorDrilldown.stocks.map((stock, i) => {
                  const signalStyles = {
                    "SURVENTE": "bg-red-500/25 text-red-300 border-red-500/40",
                    "REBOND": "bg-emerald-500/25 text-emerald-300 border-emerald-500/40",
                    "MOMENTUM": "bg-cyan-500/25 text-cyan-300 border-cyan-500/40",
                    "SURACHAT": "bg-orange-500/25 text-orange-300 border-orange-500/40",
                    "TREND UP": "bg-blue-500/25 text-blue-300 border-blue-500/40",
                    "NEUTRE": "bg-slate-700/30 text-slate-400 border-slate-600/30",
                  };
                  const rsiColor = stock.rsi > 70 ? "text-orange-400" : stock.rsi < 30 ? "text-red-400" : stock.rsi >= 50 ? "text-emerald-400" : "text-slate-400";
                  const perfColor = (v) => v >= 3 ? "text-emerald-400 font-black" : v > 0 ? "text-emerald-400/80" : v > -3 ? "text-red-400/80" : "text-red-400 font-black";

                  return (
                    <div key={stock.ticker}
                      className={`sm:grid sm:grid-cols-[1.2fr_0.5fr_0.5fr_0.5fr_0.5fr_0.5fr_0.5fr_0.5fr] gap-1.5 items-center p-2 rounded-lg border transition-all cursor-pointer hover:brightness-125 ${
                        stock.in_watchlist ? "border-slate-600/30 bg-slate-800/30" : "border-violet-500/20 bg-violet-500/[0.04]"
                      }`}
                      onClick={() => { onAnalyzeTicker(stock.ticker); onCloseSectorDrilldown(); }}
                      data-testid={`sector-stock-${stock.ticker}`}>
                      {/* Rank + Name */}
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                          i < 3 ? "bg-violet-500/30 text-violet-300 border border-violet-500/40" : "text-slate-500"
                        }`}>
                          {i + 1}
                        </span>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[11px] font-bold text-white truncate">{stock.name}</span>
                            {stock.in_watchlist ? (
                              <span className="px-1 py-0.5 rounded text-[6px] font-bold bg-slate-600/50 text-slate-400 flex-shrink-0">WL</span>
                            ) : (
                              <span className="px-1 py-0.5 rounded text-[6px] font-bold bg-violet-500/20 text-violet-300 flex-shrink-0">NEW</span>
                            )}
                          </div>
                          <span className="text-[8px] text-slate-500 font-mono">{stock.ticker}</span>
                        </div>
                      </div>

                      {/* Price */}
                      <span className="font-mono text-[11px] text-white font-bold text-right hidden sm:block">${stock.price?.toFixed(2)}</span>

                      {/* 1D */}
                      <span className={`font-mono text-[10px] text-right hidden sm:block ${perfColor(stock.perf_1d)}`}>
                        {stock.perf_1d >= 0 ? "+" : ""}{stock.perf_1d}%
                      </span>

                      {/* 3D */}
                      <span className={`font-mono text-[10px] text-right hidden sm:block ${perfColor(stock.perf_3d)}`}>
                        {stock.perf_3d >= 0 ? "+" : ""}{stock.perf_3d}%
                      </span>

                      {/* 5D */}
                      <span className={`font-mono text-[10px] text-right hidden sm:block ${perfColor(stock.perf_5d)}`}>
                        {stock.perf_5d >= 0 ? "+" : ""}{stock.perf_5d}%
                      </span>

                      {/* 1M */}
                      <span className={`font-mono text-[10px] text-right hidden sm:block ${perfColor(stock.perf_1m)}`}>
                        {stock.perf_1m >= 0 ? "+" : ""}{stock.perf_1m}%
                      </span>

                      {/* RSI */}
                      <span className={`font-mono text-[10px] text-center hidden sm:block ${rsiColor}`}>
                        {stock.rsi || "—"}
                      </span>

                      {/* Signal Swing */}
                      <div className="flex justify-center">
                        <span className={`px-1.5 py-0.5 rounded text-[7px] font-black border whitespace-nowrap ${signalStyles[stock.swing_signal] || signalStyles["NEUTRE"]}`}>
                          {stock.swing_signal || "NEUTRE"}
                        </span>
                      </div>

                      {/* Mobile compact view */}
                      <div className="flex items-center gap-2 sm:hidden mt-1">
                        <span className="font-mono text-[10px] text-white">${stock.price?.toFixed(2)}</span>
                        <span className={`text-[9px] ${perfColor(stock.perf_1d)}`}>{stock.perf_1d >= 0 ? "+" : ""}{stock.perf_1d}%</span>
                        <span className={`text-[9px] ${perfColor(stock.perf_3d)}`}>{stock.perf_3d >= 0 ? "+" : ""}{stock.perf_3d}% 3j</span>
                        <span className={`px-1 py-0.5 rounded text-[7px] font-bold ${signalStyles[stock.swing_signal] || signalStyles["NEUTRE"]}`}>
                          {stock.swing_signal}
                        </span>
                      </div>
                    </div>
                  );
                })}
                </div>

                {/* Legend */}
                <div className="flex flex-wrap gap-2 mt-3 pt-2 border-t border-slate-700/30">
                  {[
                    ["MOMENTUM", "bg-cyan-500/20 text-cyan-300", "Fort momentum + volume"],
                    ["REBOND", "bg-emerald-500/20 text-emerald-300", "RSI bas + rebond en cours"],
                    ["TREND UP", "bg-blue-500/20 text-blue-300", "Tendance haussière confirmée"],
                    ["SURVENTE", "bg-red-500/20 text-red-300", "Potentiel rebond technique"],
                    ["SURACHAT", "bg-orange-500/20 text-orange-300", "Prudence, prise de profit"],
                  ].map(([label, cls, desc]) => (
                    <span key={label} className={`px-1.5 py-0.5 rounded text-[7px] font-bold ${cls}`} title={desc}>{label}</span>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-500 text-center py-4">Aucune donnee disponible</p>
            )}
          </div>
        </div>
      )}

      {/* ===== TOP PICKS - Smart Buy Engine ===== */}
      {(topPicks?.length > 0 || swingPicks?.length > 0 || watchlistAlertsLoading) && (
        <div className="glass-card rounded-xl p-3 sm:p-5 mb-4 border border-cyan-500/30 relative overflow-hidden" data-testid="top-picks-panel">
          {/* Background glow */}
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/[0.04] via-transparent to-blue-500/[0.03]" />
          
          <div className="relative z-10">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500/30 to-blue-600/30 flex items-center justify-center border border-cyan-500/30">
                  <Crosshair className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-black text-white tracking-tight">TOP PICKS <span className="text-cyan-400/80 text-[10px] font-bold ml-1">— Momentum du jour</span></h3>
                  <p className="text-[10px] text-slate-500">
                    <span className="text-slate-400">Qui monte fort en ce moment</span> · {topPicks?.[0]?.nasdaq_trend === "ABOVE_EMA50" ? <span className="text-emerald-400">NASDAQ au-dessus EMA50</span> : <span className="text-red-400">NASDAQ sous EMA50</span>}
                    {topPicks?.[0]?.total_scanned && <span> — {topPicks[0].total_scanned}+ actions scannees</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {topPicks?.length > 0 && !topPicksAI && (
                  <button onClick={onAnalyzeTopPicksAI} disabled={topPicksAILoading}
                    className="px-3 py-1.5 bg-purple-500/15 text-purple-400 border border-purple-500/30 rounded-lg text-[10px] font-bold hover:bg-purple-500/25 transition-all flex items-center gap-1.5"
                    data-testid="analyze-top-picks-ai-btn">
                    {topPicksAILoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3" />}
                    Verdict IA
                  </button>
                )}
                <button onClick={onScanWatchlist} disabled={watchlistAlertsLoading}
                  className="px-3 py-1.5 bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 rounded-lg text-[10px] font-bold hover:bg-cyan-500/25 transition-all flex items-center gap-1.5"
                  data-testid="scan-watchlist-btn">
                  {watchlistAlertsLoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                  Scanner
                </button>
              </div>
            </div>

            {watchlistAlertsLoading && (!topPicks || topPicks.length === 0) ? (
              <div className="text-center py-6">
                <RefreshCw className="w-6 h-6 text-cyan-400 animate-spin mx-auto mb-2" />
                <p className="text-xs text-slate-400 font-medium">Scan global du marche : US + EU + Asie + ETF + Crypto...</p>
                <p className="text-[10px] text-slate-600 mt-1">Scoring /120 sur {watchlistAlertsSummary?.total_stocks || "200+"} actions - 12 criteres</p>
              </div>
            ) : topPicksAILoading ? (
              <div className="text-center py-6">
                <Brain className="w-6 h-6 text-purple-400 animate-pulse mx-auto mb-2" />
                <p className="text-xs text-slate-400 font-medium">L'IA analyse vos top picks...</p>
              </div>
            ) : (
              <>
                {/* 🆕 2026-06-04: show 'refreshing' badge inline when a scan runs over existing cached data */}
                {watchlistAlertsLoading && topPicks?.length > 0 && (
                  <div className="flex items-center gap-2 mb-3 text-[10px] text-cyan-300/80" data-testid="top-picks-refreshing-badge">
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    Mise a jour en arriere-plan (cache affiche pendant le scan)...
                  </div>
                )}
                {/* Top Picks Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3">
                  {topPicks.slice(0, 15).map((pick, i) => {
                    const aiVerdict = topPicksAI?.verdicts?.find(v => v.ticker === pick.ticker);
                    const verdictColor = aiVerdict?.verdict === "ACHETER" ? "text-cyan-400" : aiVerdict?.verdict === "ATTENDRE" ? "text-amber-400" : "text-red-400";
                    // Action-based colors for /120 scoring
                    const actionColors = {
                      "STRONG BUY": { score: "text-emerald-400", bg: "from-emerald-500/20 to-emerald-600/10 border-emerald-500/40", badge: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
                      "BUY": { score: "text-cyan-400", bg: "from-cyan-500/20 to-cyan-600/10 border-cyan-500/40", badge: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40" },
                      "WATCHLIST": { score: "text-amber-400", bg: "from-amber-500/20 to-amber-600/10 border-amber-500/40", badge: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
                      "AVOID": { score: "text-slate-400", bg: "from-slate-500/20 to-slate-600/10 border-slate-500/30", badge: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
                    };
                    const actionStyle = actionColors[pick.action] || actionColors["WATCHLIST"];
                    const confidenceColors = { "HIGH": "text-emerald-400", "MEDIUM": "text-amber-400", "LOW": "text-slate-500" };
                    // Currency symbol based on ticker
                    const curr = getTickerCurrency(pick.ticker);
                    // Region flag
                    const regionFlag = { "US": "", "EU": "", "UK": "", "ASIA": "" }[pick.region] || "";
                    // Clean ticker display
                    const cleanTicker = pick.ticker?.replace(/\.\w+$/, '') || pick.ticker;
                    return (
                      <div key={pick.ticker}
                        className={`rounded-xl p-3 border bg-gradient-to-b ${actionStyle.bg} cursor-pointer hover:brightness-125 transition-all group relative`}
                        onClick={() => onAnalyzeTicker(pick.ticker)}
                        data-testid={`top-pick-${pick.ticker}`}>
                        {/* Rank badge */}
                        <div className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-slate-800 border border-cyan-500/50 flex items-center justify-center">
                          <span className="text-[9px] font-black text-cyan-400">#{i + 1}</span>
                        </div>
                        {/* Action badge */}
                        <div className="absolute -top-1 -right-1">
                          <span className={`px-1.5 py-0.5 rounded-full text-[7px] font-black border ${actionStyle.badge}`}>
                            {pick.action}
                          </span>
                        </div>
                        
                        {/* Company name + Score */}
                        <div className="flex items-center justify-between mb-1 mt-1">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-black text-white truncate leading-tight">{pick.name}</p>
                            <div className="flex items-center gap-1 mt-0.5">
                              <span className="text-[9px] text-slate-500 font-mono">{cleanTicker}</span>
                              <span className="text-[8px]">{regionFlag}</span>
                              {pick.action === "STRONG BUY" && <Flame className="w-2.5 h-2.5 text-orange-400" />}
                            </div>
                          </div>
                          <div className="text-right ml-1">
                            <span className={`text-lg font-black ${actionStyle.score}`}>{pick.buy_score}</span>
                            <span className="text-[8px] text-slate-500">/120</span>
                          </div>
                        </div>

                        {/* Confidence + Risk */}
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className={`text-[8px] font-bold ${confidenceColors[pick.confidence] || "text-slate-500"}`}>
                            Conf. {pick.confidence}
                          </span>
                          <span className={`text-[8px] font-bold ${pick.risk_flag === "LOW" ? "text-emerald-400" : pick.risk_flag === "MEDIUM" ? "text-amber-400" : "text-red-400"}`}>
                            Risque {pick.risk_flag}
                          </span>
                        </div>
                        
                        {/* Price info */}
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="font-mono text-xs text-white font-bold">{curr}{pick.price?.toFixed(2)}</span>
                          <span className={`text-[10px] font-bold ${pick.daily_change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                            {pick.daily_change >= 0 ? "+" : ""}{pick.daily_change?.toFixed(1)}%
                          </span>
                        </div>
                        
                        {/* Key metrics row */}
                        <div className="grid grid-cols-3 gap-1 mb-2 text-[8px]">
                          <div className="text-center">
                            <span className="text-slate-500 block">RSI</span>
                            <span className={`font-bold ${pick.rsi < 30 ? "text-emerald-400" : pick.rsi > 70 ? "text-red-400" : "text-slate-300"}`}>{pick.rsi}</span>
                          </div>
                          <div className="text-center">
                            <span className="text-slate-500 block">Vol</span>
                            <span className={`font-bold ${pick.vol_ratio > 1.5 ? "text-cyan-400" : "text-slate-300"}`}>x{pick.vol_ratio}</span>
                          </div>
                          <div className="text-center">
                            <span className="text-slate-500 block">Trend</span>
                            <span className={`font-bold ${pick.trend === "UP" || pick.trend === "REVERSAL" ? "text-emerald-400" : pick.trend === "DOWN" ? "text-red-400" : "text-slate-300"}`}>{pick.trend}</span>
                          </div>
                        </div>

                        {/* Entry / Target / Stop + Budget */}
                        <div className="space-y-0.5 mb-2">
                          {pick.entry_price > 0 && (
                            <div className="flex justify-between text-[9px]">
                              <span className="text-slate-500">Entree</span>
                              <span className="text-cyan-400 font-semibold">{curr}{pick.entry_price?.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-[9px]">
                            <span className="text-slate-500">Target</span>
                            <span className="text-emerald-400 font-semibold">{curr}{pick.target?.toFixed(2)} <span className="text-emerald-500/70">+{pick.upside_pct}%</span></span>
                          </div>
                          <div className="flex justify-between text-[9px]">
                            <span className="text-slate-500">Stop</span>
                            <span className="text-red-400 font-semibold">{curr}{pick.stop_loss?.toFixed(2)}</span>
                          </div>
                          {pick.risk_reward > 0 && (
                            <div className="flex justify-between text-[9px]">
                              <span className="text-slate-500">R/R</span>
                              <span className={`font-bold ${pick.risk_reward >= 2 ? "text-emerald-400" : "text-amber-400"}`}>{pick.risk_reward}:1</span>
                            </div>
                          )}
                        </div>

                        {/* BUDGET RECOMMANDÉ */}
                        {(() => {
                          const b = calcPickBudget(totalValue, pick);
                          if (!b) return null;
                          return (
                            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-1.5 mb-2" data-testid={`budget-pick-${pick.ticker}`}>
                              <div className="flex items-center justify-between">
                                <span className="text-[8px] text-amber-400 font-bold">BUDGET</span>
                                <span className="font-mono text-xs font-black text-amber-300">{b.amount.toLocaleString()}€</span>
                              </div>
                              <div className="flex justify-between text-[7px] mt-0.5">
                                <span className="text-slate-500">{b.pct}% portefeuille</span>
                                <span className="text-emerald-400">+{b.gain.toLocaleString()}€</span>
                                <span className="text-red-400">-{b.loss.toLocaleString()}€</span>
                              </div>
                            </div>
                          );
                        })()}
                        
                        {/* Top reason */}
                        {pick.reasons?.[0] && (
                          <p className="text-[8px] text-slate-500 italic border-t border-slate-700/50 pt-1.5 mt-1">{pick.reasons[0]}</p>
                        )}
                        
                        {/* AI Verdict overlay */}
                        {aiVerdict && (
                          <div className={`mt-2 pt-2 border-t border-slate-700/50`}>
                            <div className="flex items-center justify-between">
                              <span className={`text-[10px] font-black ${verdictColor}`}>{aiVerdict.verdict}</span>
                              <div className="flex gap-0.5">
                                {[...Array(Math.min(aiVerdict.conviction || 0, 10))].map((_, j) => (
                                  <Star key={j} className={`w-2 h-2 ${j < (aiVerdict.conviction || 0) ? verdictColor : "text-slate-700"} fill-current`} />
                                ))}
                              </div>
                            </div>
                            <p className="text-[8px] text-slate-400 mt-0.5 leading-relaxed">{aiVerdict.argument}</p>
                            {aiVerdict.timing && <p className="text-[8px] text-slate-500 mt-0.5"><Clock className="w-2 h-2 inline mr-0.5" />{aiVerdict.timing}</p>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* AI Synthesis */}
                {topPicksAI?.synthese && (
                  <div className="mt-3 p-3 rounded-lg bg-purple-500/10 border border-purple-500/20" data-testid="ai-synthesis">
                    <div className="flex items-center gap-2 mb-1">
                      <Brain className="w-3.5 h-3.5 text-purple-400" />
                      <span className="text-[10px] font-bold text-purple-400 uppercase">Synthese IA</span>
                    </div>
                    <p className="text-xs text-slate-300 leading-relaxed">{topPicksAI.synthese}</p>
                  </div>
                )}

                {/* ===== OPPORTUNITIES PANEL ===== */}
                <div className="mt-4">
                  <OpportunitiesPanel onAnalyzeTicker={onAnalyzeTicker} />
                </div>

                {/* Compact alerts summary below */}
                {watchlistAlerts?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-700/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Bell className="w-3 h-3 text-amber-400" />
                      <span className="text-[10px] font-bold text-slate-400 uppercase">Alertes ({watchlistAlerts.length})</span>
                      {watchlistAlertsSummary && (
                        <div className="flex items-center gap-1 flex-wrap">
                          {watchlistAlertsSummary.buy_zone > 0 && <span className="px-1.5 py-0.5 rounded text-[8px] font-black bg-cyan-500/20 text-cyan-300">{watchlistAlertsSummary.buy_zone} achat</span>}
                          {watchlistAlertsSummary.near_buy > 0 && <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-amber-500/20 text-amber-300">{watchlistAlertsSummary.near_buy} proches</span>}
                          {watchlistAlertsSummary.oversold > 0 && <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-emerald-500/20 text-emerald-300">{watchlistAlertsSummary.oversold} RSI</span>}
                          {watchlistAlertsSummary.big_drop > 0 && <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-red-500/20 text-red-300">{watchlistAlertsSummary.big_drop} chutes</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {watchlistAlerts.slice(0, 12).map((alert, i) => {
                        const colors = {
                          "BUY_ZONE": "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
                          "NEAR_BUY_ZONE": "bg-amber-500/15 text-amber-300 border-amber-500/30",
                          "RSI_OVERSOLD": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
                          "STRONG_SIGNAL": "bg-blue-500/15 text-blue-300 border-blue-500/30",
                          "BIG_DROP": "bg-red-500/15 text-red-300 border-red-500/30",
                        };
                        return (
                          <span key={`${alert.ticker}-${alert.alert_type}`}
                            className={`px-2 py-1 rounded-md border text-[9px] font-bold cursor-pointer hover:brightness-125 transition-all ${colors[alert.alert_type] || colors["NEAR_BUY_ZONE"]}`}
                            onClick={() => onAnalyzeTicker(alert.ticker)}
                            data-testid={`watchlist-alert-${alert.ticker}`}>
                            {alert.ticker} {alert.daily_change < 0 ? `${alert.daily_change.toFixed(1)}%` : ""}
                          </span>
                        );
                      })}
                      {watchlistAlerts.length > 12 && (
                        <span className="px-2 py-1 rounded-md text-[9px] text-slate-500">+{watchlistAlerts.length - 12} autres</span>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Show scan button even when no picks */}
      {!topPicks?.length && !watchlistAlerts?.length && !watchlistAlertsLoading && (
        <div className="glass-card rounded-xl p-4 mb-4 border border-slate-700/50 flex items-center justify-between" data-testid="scan-prompt-panel">
          <div className="flex items-center gap-3">
            <Crosshair className="w-5 h-5 text-cyan-400" />
            <div>
              <p className="text-sm font-bold text-white">Identifier les meilleures opportunites</p>
              <p className="text-[10px] text-slate-500">Scannez votre watchlist pour trouver les actions a acheter</p>
            </div>
          </div>
          <button onClick={onScanWatchlist}
            className="px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-400 border border-cyan-500/30 rounded-lg text-xs font-bold hover:from-cyan-500/30 hover:to-blue-500/30 transition-all flex items-center gap-2"
            data-testid="scan-watchlist-btn-empty">
            <Eye className="w-4 h-4" /> Scanner la Watchlist
          </button>
        </div>
      )}

      {/* BROKER BREAKDOWN — moved above positions table */}
      {ownerPositions.length > 0 && (() => {
        const brokerData = {};
        ownerPositions.forEach(pos => {
          const broker = pos.source === "trading212" ? "Trading 212" : (pos.broker || "Autre");
          if (!brokerData[broker]) brokerData[broker] = { positions: [], totalValue: 0, totalCost: 0, pnl: 0 };
          const price = getPrice(pos);
          const value = toEUR(price * pos.qty, pos.ticker, broker);
          const cost = toEUR(pos.avg * pos.qty, pos.ticker, broker);
          brokerData[broker].positions.push(pos);
          brokerData[broker].totalValue += value;
          brokerData[broker].totalCost += cost;
          brokerData[broker].pnl += value - cost;
        });
        const grandTotal = Object.values(brokerData).reduce((s, b) => s + b.totalValue, 0);
        const sortedBrokers = Object.entries(brokerData).sort((a, b) => b[1].totalValue - a[1].totalValue);
        const brokerLoading = !hasPrices && quotesLoading;

        return (
          <div className="mb-4 p-3 sm:p-4 rounded-xl bg-slate-800/30 border border-slate-700/30" data-testid="broker-breakdown">
            <div className="flex items-center gap-2 mb-2 sm:mb-3 flex-wrap">
              <Layers className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-cyan-400" />
              <h3 className="text-xs sm:text-sm font-black text-white">Repartition par Courtier</h3>
              <span className="text-[9px] sm:text-[10px] text-slate-500">{sortedBrokers.length} courtiers</span>
              <span className="text-[9px] sm:text-[10px] text-slate-500 italic">· BCE ≈ 1€=${liveEurRate ? Number(liveEurRate).toFixed(4) : "1.1594"}</span>
              <div className="ml-auto text-[9px] sm:text-[10px] text-slate-500 flex items-center gap-2">
                {brokerLoading ? (
                  <span className="flex items-center gap-1.5"><RefreshCw className="w-3 h-3 text-slate-400 animate-spin" /> Chargement des prix live...</span>
                ) : (
                  <>
                    Total: <span className="text-white font-bold">{formatEUR(Math.round(grandTotal))}</span>
                    {" | "}P&L: <span className={`font-bold ${Object.values(brokerData).reduce((s, b) => s + b.pnl, 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {Object.values(brokerData).reduce((s, b) => s + b.pnl, 0) >= 0 ? "+" : ""}{formatEUR(Math.round(Object.values(brokerData).reduce((s, b) => s + b.pnl, 0)))}
                    </span>
                    {lastUpdate && (
                      <span className="text-[8px] sm:text-[9px] text-slate-600 font-mono ml-1" data-testid="broker-last-update">
                        Mis a jour: {lastUpdate.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
              {brokerFilter !== null && (
                <button onClick={() => setBrokerFilter(null)}
                  className="col-span-2 sm:col-span-2 lg:col-span-4 px-3 py-2 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-xs text-white font-bold border border-slate-600/50 transition-colors flex items-center justify-center gap-2"
                  data-testid="broker-filter-clear">
                  <span>✕ Effacer le filtre — Afficher tous les courtiers</span>
                </button>
              )}
              {sortedBrokers.map(([broker, data]) => {
                const pnlPct = data.totalCost > 0 ? ((data.pnl / data.totalCost) * 100) : 0;
                const allocationPct = grandTotal > 0 ? ((data.totalValue / grandTotal) * 100) : 0;
                const style = BROKER_STYLES[broker] || DEFAULT_BROKER_STYLE;
                const BrokerIcon = style.icon;
                const isActive = brokerFilter === broker;
                return (
                  <button
                    key={broker}
                    type="button"
                    onClick={() => setBrokerFilter(prev => prev === broker ? null : broker)}
                    className={`p-2 sm:p-3 rounded-lg sm:rounded-xl text-left transition-all cursor-pointer ${
                      isActive
                        ? `${style.bg} ${style.border} border-2 ring-2 ring-offset-2 ring-offset-slate-900 ring-white/10 shadow-lg`
                        : `bg-slate-900/50 border border-slate-700/20 hover:border-slate-500/60 hover:bg-slate-900/70`
                    }`}
                    data-testid={`broker-card-${broker.replace(/\s/g, '-')}`}
                    title={`Cliquer pour filtrer les positions sur ${broker}`}>
                    <div className="flex items-center gap-2 mb-1 sm:mb-2">
                      <div className={`flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br ${style.ring} shrink-0 shadow-md`}>
                        <BrokerIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                      </div>
                      <span className={`text-[10px] sm:text-xs font-bold truncate flex-1 ${isActive ? style.text : "text-white"}`}>{broker}</span>
                      <span className="text-[8px] sm:text-[9px] px-1 sm:px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400 font-bold shrink-0">{data.positions.length}</span>
                    </div>
                    {brokerLoading ? (
                      <div className="flex items-center gap-1.5 my-2">
                        <RefreshCw className="w-3.5 h-3.5 text-slate-400 animate-spin" />
                        <span className="text-[10px] text-slate-500">Chargement...</span>
                      </div>
                    ) : (
                      <>
                        <div className="text-sm sm:text-lg font-black text-white">{formatEUR(Math.round(data.totalValue))}</div>
                        <div className="flex items-center justify-between mt-0.5 sm:mt-1">
                          <span className={`text-[10px] sm:text-xs font-bold ${data.pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                            {data.pnl >= 0 ? "+" : ""}{formatEUR(Math.round(data.pnl))} ({pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(1)}%)
                          </span>
                          <span className="text-[9px] sm:text-[10px] text-slate-500 font-mono">{allocationPct.toFixed(0)}%</span>
                        </div>
                        <div className="mt-1 sm:mt-2 h-1 bg-slate-700/40 rounded-full overflow-hidden">
                          <div className={`h-full bg-gradient-to-r ${style.ring} rounded-full transition-all`} style={{ width: `${Math.min(100, allocationPct)}%` }} />
                        </div>
                      </>
                    )}
                    <div className="mt-1 sm:mt-2 flex flex-wrap gap-0.5 sm:gap-1">
                      {data.positions.slice(0, 12).map(p => (
                        <span key={p.ticker} className="text-[7px] sm:text-[8px] text-slate-500 font-mono">{p.ticker}</span>
                      ))}
                      {data.positions.length > 12 && (
                        <span className="text-[7px] sm:text-[8px] text-slate-600 font-mono">+{data.positions.length - 12}</span>
                      )}
                    </div>
                    {/* Per-broker FX override — click stops propagation so card click (filter) still works */}
                    <div className="mt-2 flex items-center gap-1 pt-2 border-t border-slate-700/30" onClick={(e) => e.stopPropagation()}>
                      <span className="text-[8px] sm:text-[9px] text-slate-500 font-mono">1€=$</span>
                      <input
                        type="number"
                        step="0.0001"
                        min="0.5"
                        max="2"
                        value={brokerFxOverrides[broker] ?? ""}
                        placeholder={liveEurRate ? Number(liveEurRate).toFixed(4) : "1.1594"}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => updateBrokerFxOverride && updateBrokerFxOverride(broker, e.target.value)}
                        className="flex-1 min-w-0 w-12 px-1 py-0.5 bg-slate-900/70 border border-slate-700 rounded text-[9px] sm:text-[10px] text-white font-mono focus:border-cyan-500/50 focus:outline-none"
                        data-testid={`fx-override-${broker.replace(/\s/g, '-')}`}
                        title={`Taux EUR/USD spécifique à ${broker}. Vide = taux BCE.`}
                      />
                      {brokerFxOverrides[broker] != null && (
                        <button onClick={(e) => { e.stopPropagation(); updateBrokerFxOverride && updateBrokerFxOverride(broker, null); }}
                          className="text-[9px] text-amber-400 hover:text-amber-300 font-bold shrink-0"
                          title="Reset au taux BCE">
                          ↺
                        </button>
                      )}
                    </div>
                    {isActive && (
                      <div className={`mt-2 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider ${style.text} flex items-center gap-1`}>
                        ✓ Filtre actif
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}

      <div className="glass-card rounded-xl overflow-hidden">
        <div className="px-3 sm:px-5 py-3 sm:py-4 border-b border-slate-700">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
              <h2 className="font-display text-base sm:text-lg font-bold">Positions — {owner}</h2>
              {brokerFilter && (() => {
                const style = BROKER_STYLES[brokerFilter] || DEFAULT_BROKER_STYLE;
                const BrokerIcon = style.icon;
                return (
                  <button onClick={() => setBrokerFilter(null)}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] sm:text-xs font-bold ${style.bg} ${style.text} border ${style.border} hover:opacity-80 transition-opacity`}
                    data-testid="active-broker-pill"
                    title="Cliquer pour effacer le filtre courtier">
                    <BrokerIcon className="w-3 h-3" />
                    {brokerFilter}
                    <span className="ml-1 text-slate-400">✕</span>
                  </button>
                );
              })()}
              <div className="flex items-center gap-1 bg-slate-800/80 rounded-lg p-0.5" data-testid="strategy-filter">
                <button onClick={() => setStrategyFilter("ALL")}
                  className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs font-bold transition-all ${strategyFilter === "ALL" ? "bg-slate-600 text-white" : "text-slate-400 hover:text-white"}`}>
                  Tout ({ownerPositions.length})
                </button>
                <button onClick={() => setStrategyFilter("LT")}
                  className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs font-bold transition-all ${strategyFilter === "LT" ? "bg-blue-500/30 text-blue-400 border border-blue-500/30" : "text-slate-400 hover:text-blue-300"}`}>
                  LT ({ltCount})
                </button>
                <button onClick={() => setStrategyFilter("ST")}
                  className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs font-bold transition-all ${strategyFilter === "ST" ? "bg-amber-500/30 text-amber-400 border border-amber-500/30" : "text-slate-400 hover:text-amber-300"}`}>
                  ST ({stCount})
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <span className="hidden sm:inline text-xs text-slate-400">Click row for AI analysis</span>
              <button onClick={onExpertAnalysis}
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-500/20 border border-purple-500/30 text-purple-400 rounded-lg font-semibold text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2 hover:bg-purple-500/30 transition-colors"
                data-testid="expert-analysis-btn">
                <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Smart Analyse</span>
                <span className="sm:hidden">Smart</span>
              </button>
              {onFindOpportunity && (
                <button onClick={onFindOpportunity}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 rounded-lg font-semibold text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2 hover:bg-cyan-500/30 transition-colors"
                  data-testid="find-opportunity-btn">
                  <Search className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Trouve-moi</span>
                  <span className="sm:hidden">Trouve</span>
                </button>
              )}
              <button onClick={syncTrading212} disabled={t212Syncing}
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-500/20 border border-blue-500/30 text-blue-400 rounded-lg font-semibold text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2 hover:bg-blue-500/30 transition-colors disabled:opacity-50"
                data-testid="sync-t212-btn">
                <RefreshCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${t212Syncing ? 'animate-spin' : ''}`} />
                {t212Syncing ? 'Sync...' : 'Sync T212'}
              </button>
              <button onClick={openAddModal}
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-cyan-300 to-blue-500 text-slate-900 rounded-lg font-semibold text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2 shadow-glow hover:shadow-glow-lg">
                <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />Ajouter
              </button>
            </div>
          </div>
        </div>

        {/* Portfolio Command Bar */}
        <div className="px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-800/30 border-b border-slate-700/50">
          <div className="flex gap-2 items-center">
            <Bot className="w-4 h-4 text-cyan-400 shrink-0 hidden sm:block" />
            <input value={portfolioCmd} onChange={(e) => setPortfolioCmd(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && portfolioCmd && !portfolioCmdLoading) onPortfolioCommand(); }}
              placeholder={isListening && listenTarget === "portfolio" ? "Parlez..." : "Ex: j'ai acheté 100 NVDA à 180$"}
              className={`flex-1 px-3 sm:px-4 py-2 bg-slate-900/50 border rounded-lg text-sm text-white placeholder:text-slate-600 focus:border-cyan-500/50 focus:outline-none ${isListening && listenTarget === "portfolio" ? "border-red-500 animate-pulse" : "border-slate-700"}`}
              data-testid="portfolio-command-input" />
            <button onClick={() => toggleVoice("portfolio")}
              className={`px-2.5 sm:px-3 py-2 rounded-lg text-sm flex items-center transition-all ${isListening && listenTarget === "portfolio" ? "bg-red-500 text-white shadow-lg shadow-red-500/30 animate-pulse" : "bg-slate-700 text-slate-400 hover:text-white hover:bg-slate-600"}`}
              data-testid="portfolio-command-mic">
              {isListening && listenTarget === "portfolio" ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
            <button onClick={onPortfolioCommand}
              disabled={!portfolioCmd || portfolioCmdLoading}
              className="px-3 sm:px-4 py-2 bg-cyan-500/20 text-cyan-300 rounded-lg text-sm font-bold border border-cyan-500/30 hover:bg-cyan-500/30 disabled:opacity-40 flex items-center gap-1.5"
              data-testid="portfolio-command-send">
              {portfolioCmdLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            </button>
          </div>
          {portfolioCmdResult && portfolioCmdResult.status === "error" && (
            <div className="mt-2 text-xs text-red-400 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" />{portfolioCmdResult.message}
            </div>
          )}
        </div>

        <div className="hidden md:grid px-4 py-2.5 bg-slate-800/40 border-b border-slate-700/50 table-header text-[10px]" style={{ gridTemplateColumns: '1.8fr 0.4fr 0.5fr 0.7fr 0.7fr 0.7fr 0.7fr 0.7fr 0.6fr 0.6fr 0.8fr 0.7fr 0.6fr 0.4fr' }}>
              {[
                { key: null, label: "Asset", align: "" },
                { key: null, label: "7J", align: "text-center" },
                { key: "qty", label: "Qty", align: "text-right" },
                { key: null, label: "Entry", align: "text-right" },
                { key: null, label: "Current", align: "text-right" },
                { key: "value", label: "Value", align: "text-right" },
                { key: "variation", label: "Variation", align: "text-right" },
                { key: "gp", label: "G/P", align: "text-right" },
                { key: "pnl", label: "P&L %", align: "text-right" },
                { key: null, label: "Sup / Res", align: "text-right" },
                { key: null, label: "Fair Value", align: "text-right" },
                { key: null, label: "Earnings", align: "text-right" },
                { key: null, label: "Signal", align: "text-center" },
                { key: null, label: "", align: "" },
              ].map((col, ci) => (
                <div key={ci} className={`${col.align} ${col.key ? "cursor-pointer hover:text-cyan-400 select-none flex items-center gap-0.5" : ""} ${col.align === "text-right" && col.key ? "justify-end" : ""}`}
                  onClick={() => { if (!col.key) return; if (sortCol === col.key) { setSortDir(d => d === "desc" ? "asc" : "desc"); } else { setSortCol(col.key); setSortDir("desc"); } }}>
                  {col.label}
                  {col.key && sortCol === col.key && <ChevronDown className={`w-3 h-3 transition-transform ${sortDir === "asc" ? "rotate-180" : ""}`} />}
                  {col.key && sortCol !== col.key && <ChevronDown className="w-3 h-3 opacity-20" />}
                </div>
              ))}
        </div>
        {(() => {
          const filteredByBroker = brokerFilter
            ? portfolio.filter(p => {
                const b = p.source === "trading212" ? "Trading 212" : (p.broker || "Autre");
                return b === brokerFilter;
              })
            : portfolio;
          const sorted = [...filteredByBroker].sort((a, b) => {
            if (!sortCol) return 0;
            const prA = getPrice(a), prB = getPrice(b);
            const dcA = dailyChanges[a.ticker], dcB = dailyChanges[b.ticker];
            let vA = 0, vB = 0;
            if (sortCol === "variation") { vA = dcA?.dp || 0; vB = dcB?.dp || 0; }
            else if (sortCol === "pnl") { vA = ((prA - a.avg) / a.avg) * 100; vB = ((prB - b.avg) / b.avg) * 100; }
            else if (sortCol === "gp") { vA = (prA - a.avg) * a.qty; vB = (prB - b.avg) * b.qty; }
            else if (sortCol === "value") { vA = prA * a.qty; vB = prB * b.qty; }
            else if (sortCol === "qty") { vA = a.qty; vB = b.qty; }
            return sortDir === "desc" ? vB - vA : vA - vB;
          });
          return sorted.map((pos, i) => {
          const pr = getPrice(pos);
          const sr = supportResistance[pos.ticker];
          const pnlPct = ((pr - pos.avg) / pos.avg) * 100;
          // Value & G/P always in EUR
          const prEUR = toEUR ? toEUR(pr, pos.ticker, pos.source === "trading212" ? "Trading 212" : (pos.broker || "Autre")) : pr;
          const avgEUR = toEUR ? toEUR(pos.avg, pos.ticker, pos.source === "trading212" ? "Trading 212" : (pos.broker || "Autre")) : pos.avg;
          const value = prEUR * pos.qty;
          // Display price in native currency, EXCEPT exotic (DKK, NOK, SEK) → EUR
          const nativeCur = getTickerCurrency(pos.ticker);
          const exoticCurrencies = ["kr ", "kr", "CHF ", "CHF", "¥", "₩", "HK$", "₹", "A$"];
          const isExotic = exoticCurrencies.includes(nativeCur);
          const displayPrice = isExotic ? prEUR : pr;
          const displayAvg = isExotic ? avgEUR : pos.avg;
          const displayCur = isExotic ? "€" : (nativeCur || cur);
          const signal = getSignal(pos, pr, sr);
          const smartSig = sellSignals[pos.ticker];
          const conv = convictionScores[pos.ticker];
          const dc = dailyChanges[pos.ticker];
          const dayPct = dc ? dc.dp : null;
          const strat = pos.strategy || "LT";
          const spark = sparklines[pos.ticker];
          const gainPerShare = prEUR - avgEUR;
          const gainLossMoney = gainPerShare * pos.qty;
          const earning = earningsData[pos.ticker];
          const earningDateStr = earning?.date ? new Date(earning.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : null;
          const earningDays = earning?.date ? Math.ceil((new Date(earning.date) - new Date()) / 86400000) : null;
          const pm = postMarketData[pos.ticker];
          const val = valuationData[pos.ticker];
          const coeff = val?.coefficient;
          const tickerMarket = pos.ticker.endsWith(".DE") ? marketStatus.XETRA : 
                               pos.ticker.endsWith(".PA") ? marketStatus.EURONEXT :
                               pos.ticker.endsWith(".L") ? marketStatus.LSE : marketStatus.NYSE;
          const cur = getTickerCurrency(pos.ticker);
          return (
            <div key={pos.id} data-testid={`position-row-${pos.ticker}`}>
              {/* Desktop row */}
              <div className={`hidden md:grid px-4 py-2 items-center hover:bg-cyan-500/[0.03] transition-colors ${i < sorted.length - 1 ? "border-b border-slate-700/30" : ""}`}
                style={{ gridTemplateColumns: '1.8fr 0.4fr 0.5fr 0.7fr 0.7fr 0.7fr 0.7fr 0.7fr 0.6fr 0.6fr 0.8fr 0.7fr 0.6fr 0.4fr' }}>
                <div className="flex items-center gap-2 cursor-pointer row-hover rounded-md -ml-1 pl-1 py-0.5" onClick={() => analyzePosition(pos)}>
                  <span className="text-sm">{getTickerFlag(pos.ticker, pos.flag)}</span>
                  <div className="min-w-0">
                    <div className="font-semibold text-xs text-white flex items-center gap-1">
                      <span className="truncate">{pos.name}</span>
                      <span className={`px-1 py-px rounded text-[7px] font-black ${strat === "ST" ? "bg-amber-500/15 text-amber-400" : "bg-cyan-500/15 text-cyan-400"}`}>{strat}</span>
                      {tickerMarket && <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${tickerMarket.status === "OPEN" ? "bg-cyan-400" : "bg-zinc-600"}`} />}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[9px] text-slate-500 font-mono">{pos.ticker}{pos.source === "trading212" ? " · T212" : pos.broker ? ` · ${pos.broker}` : ""}</span>
                      {conv && (
                        <span className={`inline-flex items-center gap-0.5 px-1 py-px rounded text-[7px] font-black leading-none ${
                          conv.grade === "A" ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/20" :
                          conv.grade === "B" ? "bg-cyan-500/15 text-cyan-400 border border-cyan-500/20" :
                          conv.grade === "C" ? "bg-slate-500/15 text-slate-400 border border-slate-500/20" :
                          conv.grade === "D" ? "bg-amber-500/15 text-amber-400 border border-amber-500/20" :
                          "bg-red-500/15 text-red-400 border border-red-500/20"
                        }`} title={conv.headline || `Score: ${conv.score}/100 | News: ${conv.news?.positive || 0}+ ${conv.news?.negative || 0}- | ${conv.catalysts?.join(", ") || "Pas de catalyseur"}`} data-testid={`conviction-${pos.ticker}`}>
                          {conv.grade}{conv.score}
                          {conv.catalysts?.length > 0 && <span className="opacity-70 ml-0.5">{conv.catalysts[0]}</span>}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex justify-center">
                  {spark && spark.length > 1 ? <Sparkline values={spark} width={40} height={16} /> : <span className="text-slate-700 text-[9px]">—</span>}
                </div>
                <div className="text-right font-mono text-[11px] text-slate-300">{pos.qty < 1 ? pos.qty.toFixed(4) : formatNumber(pos.qty, 0)}</div>
                <div className="text-right font-mono text-[11px] text-slate-400">{displayCur}{formatNumber(displayAvg)}</div>
                <div className="text-right" data-testid={`current-price-${pos.ticker}`}>
                  <div className="font-mono text-[11px] text-white font-medium">{displayCur}{formatNumber(displayPrice)}</div>
                  {pm?.pm ? (
                    <div className="font-mono text-[8px] font-bold text-orange-400" title="After Hours">
                      AH {cur}{formatNumber(pm.pm)} <span className={(pm.pm_dp || 0) >= 0 ? "text-emerald-400" : "text-red-400"}>{(pm.pm_dp || 0) >= 0 ? "+" : ""}{(pm.mm_dp || 0).toFixed(2)}%</span>
                    </div>
                  ) : pm?.pre ? (
                    <div className="font-mono text-[8px] font-bold text-yellow-400" title="Pre-Market">
                      PRE {cur}{formatNumber(pm.pre)} {(() => { const rc = closePrices?.[pos.ticker]; const pct = rc > 0 ? ((pm.pre - rc) / rc * 100) : (pm.pre_dp || 0); return <span className={pct >= 0 ? "text-emerald-400" : "text-red-400"}>{pct >= 0 ? "+" : ""}{pct.toFixed(2)}%</span>; })()}
                    </div>
                  ) : null}
                </div>
                <div className="text-right font-mono text-[11px] text-white" data-testid={`value-${pos.ticker}`}>
                  {prEUR ? `€${value >= 1000 ? `${(value / 1000).toFixed(1)}k` : formatNumber(value, 0)}` : "—"}
                </div>
                <div className="text-right" data-testid={`daily-change-${pos.ticker}`}>
                  {dayPct !== null ? (
                    <div className={`font-mono text-[11px] font-bold ${dayPct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {dayPct >= 0 ? "+" : ""}{dayPct.toFixed(2)}%
                    </div>
                  ) : <span className="text-slate-600 text-[9px]">—</span>}
                </div>
                <div className="text-right" data-testid={`gain-loss-money-${pos.ticker}`}>
                  <div className={`font-mono text-[11px] font-bold ${gainLossMoney >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {gainLossMoney >= 0 ? "+" : ""}{Math.abs(gainLossMoney) >= 1000 ? `${(gainLossMoney / 1000).toFixed(1)}k` : `${Math.round(gainLossMoney)}`}€
                  </div>
                </div>
                <div className={`text-right font-mono text-[11px] font-bold ${pnlPct >= 0 ? "text-emerald-400" : "text-red-400"}`}>{formatPercent(pnlPct)}</div>
                <div className="text-right" data-testid={`sup-res-${pos.ticker}`}>
                  {sr?.support || sr?.resistance ? (
                    <div className="font-mono text-[9px] leading-tight">
                      <span className="text-emerald-400/80">{sr?.support ? `${cur}${sr.support}` : "—"}</span>
                      <span className="text-slate-600 mx-0.5">/</span>
                      <span className="text-red-400/80">{sr?.resistance ? `${cur}${sr.resistance}` : "—"}</span>
                    </div>
                  ) : <span className="text-slate-700 text-[9px]">—</span>}
                </div>
                <div className="text-right" data-testid={`fair-value-${pos.ticker}`}>
                  {val?.fair_value ? (
                    <div>
                      <div className={`font-mono text-[10px] font-bold ${coeff <= 0.85 ? "text-emerald-400" : coeff <= 1.1 ? "text-cyan-400" : "text-red-400"}`}>
                        {cur}{formatNumber(val.fair_value)}
                      </div>
                      <div className={`font-mono text-[8px] ${coeff <= 0.85 ? "text-emerald-400/60" : coeff <= 1.1 ? "text-cyan-400/60" : "text-red-400/60"}`}>
                        {coeff <= 0.85 ? "Sous" : coeff <= 1.1 ? "Juste" : "Sur"} {coeff}x
                        {val.method && <span className="text-slate-500 ml-1">({val.method})</span>}
                      </div>
                    </div>
                  ) : <span className="text-slate-700 text-[9px]">—</span>}
                </div>
                <div className="text-right" data-testid={`earnings-${pos.ticker}`}>
                  {earning ? (
                    <div className={`font-mono text-[9px] font-bold ${earningDays !== null && earningDays <= 14 ? "text-amber-400" : earningDays <= 30 ? "text-cyan-400" : "text-slate-400"}`}>
                      {earningDateStr}
                      {earningDays !== null && <span className="text-slate-500 ml-0.5">{earningDays}j</span>}
                    </div>
                  ) : <span className="text-slate-700 text-[9px]">—</span>}
                </div>
                <div className="text-center" data-testid={`signal-${pos.ticker}`}>
                  {(() => {
                    const ar = analystRatings[pos.ticker];
                    if (ar && ar.verdict && ar.verdict !== "N/A") {
                      const style =
                        ar.verdict === "STRONG_BUY" ? "bg-emerald-500/25 text-emerald-300 border-emerald-400/40" :
                        ar.verdict === "BUY"        ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/25" :
                        ar.verdict === "HOLD"       ? "bg-amber-500/15 text-amber-400 border-amber-500/25" :
                        ar.verdict === "SELL"       ? "bg-red-500/15 text-red-400 border-red-500/25" :
                        "bg-red-500/25 text-red-300 border-red-400/40"; // STRONG_SELL
                      const label = ar.verdict === "STRONG_BUY" ? "STRONG BUY" : ar.verdict === "STRONG_SELL" ? "STRONG SELL" : ar.verdict;
                      const upsideCol = (ar.upside_pct ?? 0) >= 0 ? "text-emerald-400/80" : "text-red-400/80";
                      return (
                        <div title={`Consensus ${ar.num_analysts || "?"} analystes · mean ${ar.mean?.toFixed(2) || "—"} · target $${ar.target_mean?.toFixed(0) || "—"}`}>
                          <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase border ${style}`}>{label}</span>
                          {ar.upside_pct != null && <div className={`text-[8px] mt-0.5 font-mono ${upsideCol}`}>{ar.upside_pct >= 0 ? "+" : ""}{ar.upside_pct.toFixed(0)}%</div>}
                          {ar.num_analysts && <div className="text-[7px] text-slate-500">n={ar.num_analysts}</div>}
                        </div>
                      );
                    }
                    // Fallback : ancien heuristique local si Wall Street n'a rien
                    if (smartSig) {
                      return (
                        <div title={smartSig.reasons?.join(" | ")}>
                          <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${smartSig.verdict === "VENDRE" ? "bg-red-500/20 text-red-400 border border-red-500/30" : smartSig.verdict === "SURVEILLER" ? "bg-amber-500/15 text-amber-400 border border-amber-500/25" : "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25"}`}>{smartSig.verdict}</span>
                          <div className="text-[7px] mt-0.5 font-mono text-slate-500">{smartSig.confidence}%</div>
                          <div className="text-[6px] mt-px text-slate-400 truncate max-w-[80px]" title={smartSig.main_reason}>{smartSig.main_reason?.substring(0, 18)}</div>
                        </div>
                      );
                    }
                    return (
                      <>
                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${signal.label.includes("VENDRE") || signal.label.includes("STOP") || signal.label.includes("PROFIT") ? "signal-sell" : signal.label === "RENFORCER" ? "signal-buy" : signal.label === "SURVEILLER" || signal.label === "NEUTRE" || signal.label.includes("LAISSER") || signal.label.includes("PROCHE") ? "signal-watch" : "signal-hold"}`}>{signal.label}</span>
                        {pos.stop_loss && <div className="font-mono text-[8px] text-red-400/60 mt-0.5">SL {cur}{formatNumber(pos.stop_loss)}</div>}
                      </>
                    );
                  })()}
                </div>
                <div className="flex items-center justify-end gap-1">
                  <button onClick={(e) => { e.stopPropagation(); onExpertAnalyzePosition(pos); }}
                    className="p-1 text-slate-600 hover:text-purple-400 transition-colors rounded" data-testid={`committee-btn-${pos.ticker}`}
                    title="Analyse Comité d'Experts">
                    <Users className="w-3 h-3" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); setEditingPosition(pos); setModalMode("edit"); setShowPositionModal(true); }}
                    className="p-1 text-slate-600 hover:text-cyan-400 transition-colors rounded" data-testid={`edit-btn-${pos.ticker}`}>
                    <Settings className="w-3 h-3" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleDeletePosition(pos.id); }}
                    className="p-1 text-slate-600 hover:text-red-400 transition-colors rounded" data-testid={`delete-btn-${pos.ticker}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
              {/* Mobile card */}
              <div className={`md:hidden px-3 py-2.5 ${i < sorted.length - 1 ? "border-b border-slate-700/30" : ""}`}
                onClick={() => analyzePosition(pos)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="text-base">{getTickerFlag(pos.ticker, pos.flag)}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-sm text-white truncate">{pos.name}</span>
                        <span className={`px-1 py-px rounded text-[7px] font-black shrink-0 ${strat === "ST" ? "bg-amber-500/15 text-amber-400" : "bg-cyan-500/15 text-cyan-400"}`}>{strat}</span>
                        {tickerMarket && <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${tickerMarket.status === "OPEN" ? "bg-cyan-400" : "bg-zinc-600"}`} />}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-slate-500 font-mono">{pos.ticker}{pos.source === "trading212" ? " · T212" : pos.broker ? ` · ${pos.broker}` : ""}</span>
                        {conv && (
                          <span className={`px-1 py-px rounded text-[7px] font-black ${
                            conv.grade === "A" ? "bg-emerald-500/15 text-emerald-400" :
                            conv.grade === "B" ? "bg-cyan-500/15 text-cyan-400" :
                            conv.grade === "C" ? "bg-slate-500/15 text-slate-400" :
                            conv.grade === "D" ? "bg-amber-500/15 text-amber-400" :
                            "bg-red-500/15 text-red-400"
                          }`}>{conv.grade}{conv.score}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-sm text-white font-bold">€{prEUR ? (value >= 1000 ? `${(value / 1000).toFixed(1)}k` : formatNumber(value, 0)) : "—"}</div>
                    <div className={`font-mono text-xs font-bold ${pnlPct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(2)}%
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-1.5 gap-2">
                  <div className="flex items-center gap-3 text-[10px] font-mono">
                    <span className="text-slate-400">{formatNumber(pos.qty, 0)} × {displayCur}{formatNumber(displayAvg)}</span>
                    <span className="text-slate-600">→</span>
                    <span className="text-white">{displayCur}{formatNumber(displayPrice)}</span>
                    {dayPct !== null && (
                      <span className={`font-bold ${dayPct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        {dayPct >= 0 ? "+" : ""}{dayPct.toFixed(2)}%
                      </span>
                    )}
                    <span className={`font-bold ${gainLossMoney >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {gainLossMoney >= 0 ? "+" : ""}{Math.abs(gainLossMoney) >= 1000 ? `${(gainLossMoney / 1000).toFixed(1)}k` : `${Math.round(gainLossMoney)}`}€
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const ar = analystRatings[pos.ticker];
                      if (ar && ar.verdict && ar.verdict !== "N/A") {
                        const style =
                          ar.verdict === "STRONG_BUY" ? "bg-emerald-500/25 text-emerald-300 border-emerald-400/40" :
                          ar.verdict === "BUY"        ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/25" :
                          ar.verdict === "HOLD"       ? "bg-amber-500/15 text-amber-400 border-amber-500/25" :
                          ar.verdict === "SELL"       ? "bg-red-500/15 text-red-400 border-red-500/25" :
                          "bg-red-500/25 text-red-300 border-red-400/40";
                        const label = ar.verdict === "STRONG_BUY" ? "STRONG BUY" : ar.verdict === "STRONG_SELL" ? "STRONG SELL" : ar.verdict;
                        return (
                          <span title={`Consensus ${ar.num_analysts || "?"} analystes · upside ${ar.upside_pct ?? "—"}%`}
                                className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase border ${style}`}>{label}{ar.upside_pct != null ? ` ${ar.upside_pct >= 0 ? "+" : ""}${ar.upside_pct.toFixed(0)}%` : ""}</span>
                        );
                      }
                      if (smartSig) {
                        return <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${smartSig.verdict === "VENDRE" ? "bg-red-500/20 text-red-400 border border-red-500/30" : smartSig.verdict === "SURVEILLER" ? "bg-amber-500/15 text-amber-400 border border-amber-500/25" : "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25"}`}>{smartSig.verdict} {smartSig.confidence}%</span>;
                      }
                      return <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${signal.label.includes("VENDRE") || signal.label.includes("STOP") || signal.label.includes("PROFIT") ? "signal-sell" : signal.label === "RENFORCER" ? "signal-buy" : signal.label === "SURVEILLER" || signal.label === "NEUTRE" || signal.label.includes("LAISSER") || signal.label.includes("PROCHE") ? "signal-watch" : "signal-hold"}`}>{signal.label}</span>;
                    })()}
                    <button onClick={(e) => { e.stopPropagation(); onExpertAnalyzePosition(pos); }}
                      className="p-1 text-slate-600 hover:text-purple-400" data-testid={`committee-btn-mobile-${pos.ticker}`}>
                      <Users className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setEditingPosition(pos); setModalMode("edit"); setShowPositionModal(true); }}
                      className="p-1 text-slate-600 hover:text-cyan-400" data-testid={`edit-btn-mobile-${pos.ticker}`}>
                      <Settings className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })})()}
      </div>
    </div>
  );
})
